import { isFraxlend } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Fraxlend — a variable-rate isolated pair, so almost everything the generic
 * pool model says is right. ONE property is not, and it is the one a borrower
 * would be hurt by not knowing:
 *
 *   **`maxLTV` IS the liquidation threshold.** Fraxlend has a single ratio,
 *   not the usual LTV-then-LT pair. `_isSolvent` checks the borrow against the
 *   same `maxLTV` that gates opening, so borrowing to the maximum means
 *   opening a position that is ALREADY at the liquidation point — a one-wei
 *   adverse price move makes it liquidatable.
 *
 * Every other lender we carry leaves a buffer (Aave: borrow at 75 %, liquidate
 * at 80 %), and the generic sheet's "if the collateral value falls past the
 * liquidation threshold…" wording quietly implies one here too. That is exactly
 * the "ships a sheet that describes it as an ordinary pool" failure the
 * adapter-completeness guard exists to catch.
 *
 * Two smaller deviations worth stating, both from the contract:
 *
 *  - **The oracle is a two-sided BAND, and it can be stale.**
 *    `exchangeRateInfo` returns a low and a high rate and the pair uses the
 *    conservative side per direction. The rate only refreshes when someone
 *    interacts, and Fraxlend is quiet (~2.4 tx/day protocol-wide), so a
 *    position can be valued off a price that is days old.
 *  - **Freezing is a CAP, not a pause.** v3.1.0 has no deposit/borrow pause
 *    flag; governance stops new business by zeroing `depositLimit` /
 *    `borrowLimit`. Exits stay open, so a frozen market is still closable.
 *
 * The liquidation fee itself is ordinary (a liquidator bonus), so it is left to
 * the generic path rather than restated here.
 */
export const fraxlendAdapter: TermAdapter = {
  id: 'fraxlend',
  matches: isFraxlend,
  profileId: () => 'fraxlend.pair@v1',
  build: () => ({
    borrow: {
      debtShape: 'accruing',
      liquidation: {
        model: 'threshold',
        trigger: 'price',
        // The whole reason this adapter exists: opening at the cap IS opening
        // at the liquidation point.
        noBuffer: true,
      },
    },
  }),
}
